// ============================================================================
// securityRoles.ts — SSoT клиентских проверок СИСТЕМНЫХ РОЛЕЙ безопасности
// (VPN/Remote). Имена ролей — те же, что создаёт ЯДРО в каждой базе
// (zdc-server/internal/auth/system_entities.go, EnsureSystemEntities).
//
// Правило единое: администратор (роль 'admin' / разрешение 'global:admin') —
// всегда; иначе решает системная роль из JWT (user.roles). Скрытых admin-
// дефолтов НЕТ (инвариант 2026-09-28): права только из JWT/разрешений.
//
// Серверные гейты (VpnService/RemoteService) — SSoT доступа; эти проверки —
// UX-скрытие/явный экран отказа (как принято в клиенте платформы).
// ============================================================================

import { useAuthStore } from '@/store/authStore';

/** «VPN-клиент» (ядро, vpn_client): использование подключений, настроенных
 *  администратором (список доступных, конфигурация для устройства, сессии). */
export const VPN_CLIENT_ROLE = 'vpn_client';

/** «VPN-управление» (ядро, vpn_manage): управление VPN прямо в Предприятии
 *  (профили/доступ/политика/журнал); включает клиентские возможности. */
export const VPN_MANAGE_ROLE = 'vpn_manage';

/** «Remote-клиент» (ядро, remote_client): использование «Удалённого доступа»
 *  (сеансы и просмотр экрана). */
export const REMOTE_CLIENT_ROLE = 'remote_client';

/** «Remote-управление» (ядро, remote_manage): управление сеансами/гостевыми
 *  ссылками/настройками; включает клиентские возможности. */
export const REMOTE_MANAGE_ROLE = 'remote_manage';

/** «Психолог» (модуль hydra.medic.psy): рабочее место специалиста. */
export const PSY_ROLE = 'Психолог';

/** «Психолог-стажёр» (модуль hydra.medic.psy): чтение и заметки. */
export const PSY_INTERN_ROLE = 'Психолог-стажёр';

/** Клинические роли ядра «ГидраМедик» (hydra.medic.core). */
export const MEDIC_DOCTOR_ROLE = 'Врач';
export const MEDIC_REGISTRAR_ROLE = 'Медицинский регистратор';
export const MEDIC_NURSE_ROLE = 'Медсестра';

/** Роли специалистов психологии (hydra.medic.psy). */
export const PSY_SPECIALIST_ROLES = [PSY_ROLE, PSY_INTERN_ROLE] as const;

export interface SecurityAccess {
  /** Администратор: роль 'admin' или разрешение 'global:admin'. */
  isAdmin: boolean;
  /** Имена ролей текущего пользователя (из JWT). */
  roles: string[];
}

function readAccess(user: unknown): SecurityAccess {
  const u = user as { roles?: string[]; permissions?: string[] } | null;
  const roles = u?.roles || [];
  const isAdmin = roles.includes('admin') || (u?.permissions || []).includes('global:admin');
  return { isAdmin, roles };
}

/** useSecurityAccess — доступ текущего пользователя (роли JWT + админ). */
export function useSecurityAccess(): SecurityAccess {
  const user = useAuthStore((s) => s.user);
  return readAccess(user);
}

/** canUseVpn — использование VPN: админ ИЛИ «VPN-клиент»/«VPN-управление»
 *  (управление включает клиентские возможности — как на сервере). */
export function canUseVpn(access: SecurityAccess): boolean {
  return access.isAdmin
    || access.roles.includes(VPN_CLIENT_ROLE)
    || access.roles.includes(VPN_MANAGE_ROLE);
}

/** canManageVpn — управление VPN: админ ИЛИ роль «VPN-управление». */
export function canManageVpn(access: SecurityAccess): boolean {
  return access.isAdmin || access.roles.includes(VPN_MANAGE_ROLE);
}

/** canUseRemote — использование Remote: админ ИЛИ «Remote-клиент»/
 *  «Remote-управление» (обе роли дают клиентские возможности). */
export function canUseRemote(access: SecurityAccess): boolean {
  return access.isAdmin
    || access.roles.includes(REMOTE_CLIENT_ROLE)
    || access.roles.includes(REMOTE_MANAGE_ROLE);
}

/** canManageRemote — управление Remote (сеансы/гостевые ссылки/настройки):
 *  админ ИЛИ роль «Remote-управление». */
export function canManageRemote(access: SecurityAccess): boolean {
  return access.isAdmin || access.roles.includes(REMOTE_MANAGE_ROLE);
}

/** canUsePsy — рабочее место психолога: админ ИЛИ роль «Психолог»/
 *  «Психолог-стажёр» (модуль hydra.medic.psy). */
export function canUsePsy(access: SecurityAccess): boolean {
  return access.isAdmin
    || access.roles.includes(PSY_ROLE)
    || access.roles.includes(PSY_INTERN_ROLE);
}

/** canUseMedicAppointments — кабинет записи на приём: пациент, клинические
 *  роли ядра и специалисты психологии. Серверные права на объекты — SSoT. */
export function canUseMedicAppointments(access: SecurityAccess): boolean {
  return access.isAdmin
    || access.roles.includes('patient')
    || access.roles.includes(MEDIC_DOCTOR_ROLE)
    || access.roles.includes(MEDIC_REGISTRAR_ROLE)
    || access.roles.includes(MEDIC_NURSE_ROLE)
    || PSY_SPECIALIST_ROLES.some((role) => access.roles.includes(role));
}
